import React, { useEffect, useState } from 'react'
import { Card, Button, Table, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import axios from 'axios'
import { API_URL } from '../../server'
import { useParams, useNavigate } from 'react-router-dom'
import { Spinner } from 'reactstrap'
import originsDirect from '../../assets/images/ORIGINS-DIRECT.png'

const OrderResponse = () => {
  const { orderId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [orderData, setOrderData] = useState(null)
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [showAcceptModal, setShowAcceptModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)

  const getData = () => {
    setLoading(true)
    setError(null)
    // Make request without auth headers using axios directly
    axios
      .get(`${API_URL}/invoicesHeaders/orderDetails/${orderId}`)
      .then((response) => {
        setLoading(false)
        console.log('Order data received:', response.data)
        console.log('Products data:', response.data.products)
        if (response.data.products && response.data.products.length > 0) {
          console.log('First product:', response.data.products[0])
        }
        setOrderData(response.data)
        // If order is already processed, redirect
        if (response.data.status !== 'Pending') {
          if (response.data.status === 'Cancelled') {
            navigate(`/pharmacy-order-rejected?orderId=${orderId}`)
          } else if (response.data.status === 'Confirmed') {
            navigate(`/pharmacy-order-approved?orderId=${orderId}`)
          } else {
            navigate('/already-responded')
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching Order Data:', error)
        setLoading(false)
        if (error.response?.status === 404) {
          navigate('/404')
        } else if (error.response?.status === 400) {
          // Order already processed
          const status = error.response?.data?.status
          if (status === 'Cancelled') {
            navigate(`/pharmacy-order-rejected?orderId=${orderId}`)
          } else if (status === 'Confirmed') {
            navigate(`/pharmacy-order-approved?orderId=${orderId}`)
          } else {
            navigate('/already-responded')
          }
        } else {
          setError('Failed to load order details. Please try again.')
        }
      })
  }

  useEffect(() => {
    getData()
  }, [orderId])

  const handleAcceptClick = () => {
    if (processing) return // Prevent double submission
    setShowAcceptModal(true)
  }

  const handleRejectClick = () => {
    if (processing) return // Prevent double submission
    setShowRejectModal(true)
  }

  const confirmAccept = () => {
    setShowAcceptModal(false)
    setProcessing(true)
    window.location.href = `${API_URL}/invoicesHeaders/changeStatus/${orderId}?status=accepted`
  }

  const confirmReject = () => {
    setShowRejectModal(false)
    setProcessing(true)
    window.location.href = `${API_URL}/invoicesHeaders/changeStatus/${orderId}?status=declined`
  }

  const handleLogin = () => {
    window.location.href = `/manufacturer-login?redirect=${encodeURIComponent(`/manufacturer/manufacturerOrders/${orderId}`)}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00'
    return `$${parseFloat(amount).toFixed(2)}`
  }

  return (
    <>
      <div className='min-vh-100 d-flex justify-content-center align-items-center'>
        {loading ? (
          <div
            className='d-flex justify-content-center align-items-center'
            style={{ height: '70vh' }}
          >
            <Spinner color='primary' />
          </div>
        ) : error ? (
          <Card className='w-100 m-3' style={{ maxWidth: '1300px' }}>
            <main className='text-center p-4'>
              <p className='text-danger'>{error}</p>
              <Button color='primary' onClick={getData}>
                Retry
              </Button>
            </main>
          </Card>
        ) : orderData ? (
          <div
            className='justify-content-center w-full'
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div className='w-full mx-auto flex justify-center'>
              <img
                src={originsDirect}
                alt='Origins Direct'
                height='60'
                className='mx-auto'
              />
            </div>
            <Card className='w-100 m-3' style={{ maxWidth: '95%', width: '100%' }}>
              <main className='p-4'>
                <section>
                  <h1 className='h4 text-center mb-3 mt-4'>
                    Order Review & Approval
                  </h1>
                  <p className='text-center mb-4'>
                    A new order has been placed for your products. Please review the details below and accept or reject the order:
                  </p>
                  <div style={{ margin: '30px auto', width: 'fit-content' }}>
                    <p>
                      <strong>Order ID:</strong> {orderData.supplierOrderNo}
                      <br />
                      <strong>Pharmacy:</strong> {orderData.pharmacy.name}
                      <br />
                      <strong>Manufacturer Code:</strong>{' '}
                      {orderData.manufacturerCode || 'N/A'}
                      <br />
                      <strong>Order Date:</strong> {formatDate(orderData.orderDate)}
                      <br />
                      <strong>Expected Delivery:</strong>{' '}
                      {formatDate(orderData.shipDate)}
                      <br />
                    </p>
                  </div>
                </section>

                <section className='mt-4'>
                  <h2 className='h5 mb-3'>Products:</h2>
                  <div className='table-responsive'>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>UPC</th>
                          <th>Product Name</th>
                          <th>DIN No</th>
                          <th>Price</th>
                          <th>Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderData.products && orderData.products.length > 0 ? (
                          orderData.products.map((product, index) => {
                            // Debug logging
                            if (index === 0) {
                              console.log('Rendering product:', product)
                            }
                            return (
                              <tr key={index}>
                                <td>{product.retailUpcCode || 'N/A'}</td>
                                <td>{product.itemDescription || 'N/A'}</td>
                                <td>
                                  {product.drugIdentificationNo && product.drugIdentificationNo !== 'N/A'
                                    ? String(product.drugIdentificationNo).padStart(8, '0')
                                    : 'N/A'}
                                </td>
                                <td>{formatCurrency(product.unitPrice)}</td>
                                <td>{product.requestedQuantity}</td>
                              </tr>
                            )
                          })
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">No products found</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </section>

                <div
                  className='mt-4 p-3'
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderLeft: '4px solid #626ed4',
                    borderRadius: '4px',
                  }}
                >
                  <h3 className='h5' style={{ color: '#626ed4' }}>
                    Total Order Amount
                  </h3>
                  <p
                    className='mb-0'
                    style={{ fontSize: '20px', fontWeight: 'bold' }}
                  >
                    {formatCurrency(orderData.totalInvoiceAmount)}
                  </p>
                </div>

                <div className='text-center mt-5 d-flex justify-content-center gap-3 col-12 flex-wrap'>
                  <Button
                    className='btn btn-danger'
                    size='lg'
                    onClick={handleRejectClick}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Reject Order'}
                  </Button>
                  <Button
                    className='btn btn-outline-primary'
                    size='lg'
                    onClick={handleLogin}
                    disabled={processing}
                  >
                    Log in to Edit
                  </Button>
                  <Button
                    color='success'
                    size='lg'
                    onClick={handleAcceptClick}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Accept Order'}
                  </Button>
                </div>
              </main>
            </Card>
          </div>
        ) : null}
      </div>

      {/* Accept Order Confirmation Modal */}
      <Modal isOpen={showAcceptModal} toggle={() => setShowAcceptModal(false)} centered>
        <ModalHeader toggle={() => setShowAcceptModal(false)}>
          Confirm Order Acceptance
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to accept this order?</p>
          {orderData && (
            <div className='mt-3'>
              <p className='mb-1'><strong>Order ID:</strong> {orderData.supplierOrderNo}</p>
              <p className='mb-1'><strong>Pharmacy:</strong> {orderData.pharmacy?.name}</p>
              <p className='mb-0'><strong>Total Amount:</strong> {formatCurrency(orderData.totalInvoiceAmount)}</p>
            </div>
          )}
          <p className='mt-3 mb-0 text-warning'>
            <strong>This action cannot be undone.</strong>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setShowAcceptModal(false)}>
            Cancel
          </Button>
          <Button color='success' onClick={confirmAccept}>
            Accept Order
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Order Confirmation Modal */}
      <Modal isOpen={showRejectModal} toggle={() => setShowRejectModal(false)} centered>
        <ModalHeader toggle={() => setShowRejectModal(false)}>
          Confirm Order Rejection
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to reject this order?</p>
          {orderData && (
            <div className='mt-3'>
              <p className='mb-1'><strong>Order ID:</strong> {orderData.supplierOrderNo}</p>
              <p className='mb-1'><strong>Pharmacy:</strong> {orderData.pharmacy?.name}</p>
              <p className='mb-0'><strong>Total Amount:</strong> {formatCurrency(orderData.totalInvoiceAmount)}</p>
            </div>
          )}
          <p className='mt-3 mb-0 text-danger'>
            <strong>This action cannot be undone.</strong>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button color='danger' onClick={confirmReject}>
            Reject Order
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default OrderResponse

